import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  ExclamationCircleOutlined,
  LockFilled,
} from '@ant-design/icons';
import {
  Card,
  Col,
  Divider,
  Pagination,
  Popconfirm,
  Popover,
  Row,
  Segmented,
  Skeleton,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  ContextButton,
  IconContextButton,
  LinkButton,
  ScopeButton,
} from '../components/buttons';
import { AccountTypeTag } from './AccountTypeTag';
import { IconCross, IconEllipses, IconEye, IconHistory, IconToggle } from './icons';
import { applyUsersFilters, UsersTableFilters, type UsersFilterState } from './UsersTableFilters';
import { BoldNameSpan } from './tags';
import { AvatarSizes, renderAvatar } from '../avatars/renderAvatar';
import { UserAvatar } from './UserAvatar';
import {
  AccountType,
  PermissionType,
  resolveAccountType,
  type PermissionTypeValue,
  type SharedUser,
} from './types';

const isOwnerAccount = (record: SharedUser) =>
  resolveAccountType(record.accountType) === AccountType.OWNER;

export type UsersViewMode = 'list' | 'card';

type UsersManagementProps = {
  loading: boolean;
  members?: SharedUser[];
  currentUser?: SharedUser;
  hasManagerViewAccess?: boolean;
  onUpgradeClick?: () => void;
  saveUserPermissions: (
    accountUuid: string,
    permissionType: PermissionTypeValue,
    enabled: boolean,
  ) => void;
  openMemberWindow: () => void;
  openHistoryWindow: (userId: string, userName: string) => void;
  openRightsWindow: (accountId: string, userName: string) => void;
  deleteUser: (userToDeleteId: string) => void;
  resendActivation: (email: string) => void;
  viewMode: UsersViewMode;
  onViewModeChange: (mode: UsersViewMode) => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  profilePath: (accountUuid: string) => string;
  formatLastLogin?: (iso: string) => string;
};

export const UsersManagement = ({
  loading,
  members,
  currentUser,
  hasManagerViewAccess = true,
  onUpgradeClick,
  saveUserPermissions,
  openMemberWindow,
  openHistoryWindow,
  openRightsWindow,
  deleteUser,
  resendActivation,
  viewMode,
  onViewModeChange,
  pageSize,
  onPageSizeChange,
  profilePath,
  formatLastLogin,
}: UsersManagementProps) => {
  const { t } = useTranslation();
  const [filtersState, setFiltersState] = useState<UsersFilterState>({});
  const [cardPage, setCardPage] = useState(1);
  const [sortState, setSortState] = useState<{
    columnKey: string;
    order: 'ascend' | 'descend';
  }>({ columnKey: 'fullName', order: 'ascend' });
  const [permissions, setPermissions] = useState<
    Record<string, { orgs: boolean; users: boolean; manager: boolean }>
  >({});

  const permissionSnapshot = useMemo(
    () =>
      (members ?? [])
        .map(
          (m) =>
            `${m.accountUuid}:${Number(!!m.canManageOrgs)}:${Number(!!m.canManageUsers)}:${Number(!!m.managerViewEnabled)}`,
        )
        .join('|'),
    [members],
  );

  useEffect(() => {
    const perms: Record<string, { orgs: boolean; users: boolean; manager: boolean }> =
      {};
    members?.forEach((m) => {
      perms[m.accountUuid] = {
        orgs: m.canManageOrgs,
        users: m.canManageUsers,
        manager: m.managerViewEnabled || false,
      };
    });
    setPermissions(perms);
    // Sync from server permission fields only — a new members array with the same
    // flags must not reset an in-flight toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- permissionSnapshot
  }, [permissionSnapshot]);

  const displayMembers = useMemo(() => {
    const filtered = applyUsersFilters(members ?? [], filtersState);
    const direction = sortState.order === 'descend' ? -1 : 1;
    const loginTime = (value?: string | null) => {
      if (!value) return 0;
      const time = new Date(value).getTime();
      return Number.isNaN(time) ? 0 : time;
    };
    const compare = (a: SharedUser, b: SharedUser) => {
      switch (sortState.columnKey) {
        case 'accountType':
          return String(resolveAccountType(a.accountType)).localeCompare(
            String(resolveAccountType(b.accountType)),
          );
        case 'lastLogin':
          return loginTime(a.lastLogin) - loginTime(b.lastLogin);
        default:
          return (`${a.firstName} ${a.lastName}`.trim() || a.email).localeCompare(
            `${b.firstName} ${b.lastName}`.trim() || b.email,
          );
      }
    };
    return [...filtered].sort((a, b) => compare(a, b) * direction);
  }, [members, filtersState, sortState]);

  useEffect(() => {
    setCardPage(1);
  }, [filtersState]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(displayMembers.length / pageSize) || 1);
    if (cardPage > maxPage) setCardPage(maxPage);
  }, [displayMembers.length, pageSize, cardPage]);

  const paginatedCardMembers = useMemo(() => {
    const start = (cardPage - 1) * pageSize;
    return displayMembers.slice(start, start + pageSize);
  }, [displayMembers, cardPage, pageSize]);

  const userDisplayName = (record: SharedUser) =>
    `${record.firstName} ${record.lastName}`.trim() || record.email;

  const isStaff = (record: SharedUser) =>
    resolveAccountType(record.accountType) === AccountType.STAFF;

  const renderSortIcon = ({ sortOrder }: { sortOrder?: string | null }) => (
    <span
      style={{
        display: 'inline-flex',
        width: 16,
        justifyContent: 'center',
      }}
    >
      {sortOrder === 'descend' ? (
        <ArrowUpOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />
      ) : (
        <ArrowDownOutlined
          style={{
            color: 'var(--color-text-muted, #5a6a6a)',
            opacity: sortOrder === 'ascend' ? 1 : 0.45,
          }}
        />
      )}
    </span>
  );

  const lastLoginText = (record: SharedUser) => {
    if (!record.accountActivated) return t('common.never');
    if (formatLastLogin) return formatLastLogin(record.lastLogin);
    if (!record.lastLogin) return t('common.never');
    try {
      return new Date(record.lastLogin).toLocaleString();
    } catch {
      return record.lastLogin;
    }
  };

  // Status tags use the `rows-tag*` classes each app defines (colours come from tokens).
  // Invited users get a tag plus a "Resend email" text link, not a filled button.
  const renderStatus = (record: SharedUser) =>
    record.accountActivated ? (
      <Tag className="rows-tag rows-tag--success rows-tag--dot">
        {t('common.active')}
      </Tag>
    ) : (
      <Space size={10} align="center" className="users-status-invited">
        <Tag className="rows-tag rows-tag--warning rows-tag--dot">
          {t('users.invited')}
        </Tag>
        <LinkButton
          type="link"
          className="users-resend-link"
          onClick={() => resendActivation(record.email)}
        >
          {t('users.resend-email')}
        </LinkButton>
      </Space>
    );

  const renderUserIdentity = (record: SharedUser, large?: boolean) => {
    const profile = profilePath(record.accountUuid);
    const isOtherOwner =
      resolveAccountType(record.accountType) === AccountType.OWNER &&
      currentUser?.userUuid !== record.userUuid;
    const name = (
      <BoldNameSpan style={large ? { fontSize: 16, fontWeight: 600 } : undefined}>
        {`${record.firstName} ${record.lastName}`}
      </BoldNameSpan>
    );
    return (
      <Space className="users-identity" style={{ maxWidth: '100%' }}>
        <UserAvatar
          firstName={record.firstName}
          lastName={record.lastName}
          src={record.profilePicture}
          userUuid={record.userUuid}
          size={large ? 40 : 32}
        />
        <Space direction="vertical" style={{ rowGap: 0, minWidth: 0 }}>
          {isOtherOwner ? (
            name
          ) : (
            <Link to={profile} style={{ textDecoration: 'none', color: 'inherit' }}>
              {name}
            </Link>
          )}
          <BoldNameSpan fontWeight={400}>{record.email}</BoldNameSpan>
        </Space>
      </Space>
    );
  };

  const updatePerm = (
    accountUuid: string,
    key: 'orgs' | 'users' | 'manager',
    type: PermissionTypeValue,
    enabled: boolean,
  ) => {
    saveUserPermissions(accountUuid, type, enabled);
    setPermissions((prev) => ({
      ...prev,
      [accountUuid]: { ...prev[accountUuid], [key]: enabled },
    }));
  };

  const staffSwitch = (
    record: SharedUser,
    key: 'orgs' | 'users' | 'manager',
    type: PermissionTypeValue,
    label: string,
    locked?: boolean,
  ) => {
    if (record.isLicenseOwner || !isStaff(record)) return null;
    if (locked) {
      return (
        <Space>
          <Switch disabled checked={!!permissions[record.accountUuid]?.[key]} style={{ opacity: 0.5 }} />
          <LockFilled
            style={{ color: 'var(--color-text-muted, #5a6a6a)', fontSize: 12, cursor: 'pointer' }}
            onClick={onUpgradeClick}
          />
        </Space>
      );
    }
    const checked = permissions[record.accountUuid]?.[key] ?? false;
    return (
      <Tooltip
        title={
          checked
            ? t('common.toggle-on-click-to-turn-off')
            : t('common.toggle-off-click-to-turn-on')
        }
      >
        <span
          className="users-permission-switch"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <Switch
            aria-label={`${label}: ${userDisplayName(record)}`}
            className={checked ? 'switch-checked' : ''}
            checked={checked}
            onChange={(enabled) =>
              updatePerm(record.accountUuid, key, type, enabled)
            }
          />
        </span>
      </Tooltip>
    );
  };

  const contextMenu = (record: SharedUser) => (
    <div className="context-menu-items">
      <Space direction="vertical" style={{ rowGap: 0 }}>
        <IconContextButton type="text" icon={<IconEye style={{ width: 18, height: 22, marginRight: 4 }} />}>
          <Link style={{ textDecoration: 'none' }} to={profilePath(record.accountUuid)}>
            {t('users.view-details')}
          </Link>
        </IconContextButton>
        {record.accountActivated && record.lastLogin && (
          <IconContextButton
            type="text"
            onClick={() =>
              openHistoryWindow(record.userUuid, `${record.firstName} ${record.lastName}`)
            }
            icon={<IconHistory style={{ width: 18, height: 20, marginRight: 5 }} />}
          >
            {t('users.login-history')}
          </IconContextButton>
        )}
        {currentUser?.canManageUsers && !isOwnerAccount(record) && (
          <IconContextButton
            type="text"
            onClick={() =>
              openRightsWindow(record.accountUuid, `${record.firstName} ${record.lastName}`)
            }
            icon={<IconToggle style={{ width: 20, height: 20, marginRight: 3 }} />}
          >
            {t('users.access-rights')}
          </IconContextButton>
        )}
        {currentUser?.canManageUsers && !isOwnerAccount(record) && (
          <Popconfirm
            title={t('users.delete-user-title')}
            description={
              <span>
                {t('users.delete-user-description').split('{br}')[0]}
                <br />
                {t('users.delete-user-description').split('{br}')[1]}
              </span>
            }
            onConfirm={() => deleteUser(record.userUuid)}
            okText={t('common.yes')}
            cancelText={t('common.cancel')}
          >
            <IconContextButton
              type="text"
              danger
              icon={<IconCross style={{ width: 10, height: 22, marginRight: 8, marginLeft: 5 }} />}
            >
              {t('users.delete-user')}
            </IconContextButton>
          </Popconfirm>
        )}
      </Space>
    </div>
  );

  const renderButtons = (record: SharedUser) => {
    if (
      resolveAccountType(record.accountType) === AccountType.OWNER &&
      currentUser?.userUuid !== record.userUuid
    ) {
      return null;
    }
    const hideAccessRights = isOwnerAccount(record);
    return (
      <Space size={8} align="center">
        {currentUser?.canManageUsers && (
          <ScopeButton
            type="default"
            className="users-access-rights"
            width="auto"
            height="26px"
            fontSize="11px"
            lineheight="12px"
            aria-hidden={hideAccessRights || undefined}
            tabIndex={hideAccessRights ? -1 : 0}
            onClick={(e) => {
              e?.stopPropagation?.();
              if (hideAccessRights) return;
              openRightsWindow(record.accountUuid, `${record.firstName} ${record.lastName}`);
            }}
            style={{
              whiteSpace: 'nowrap',
              paddingInline: 10,
              visibility: hideAccessRights ? 'hidden' : 'visible',
            }}
          >
            {t('users.access-rights')}
          </ScopeButton>
        )}
        <Popover
          placement="topLeft"
          arrow={false}
          trigger="click"
          content={contextMenu(record)}
        >
          <ContextButton
            type="text"
            aria-label={t('common.more-actions')}
            onClick={(e) => e?.stopPropagation?.()}
          >
            <IconEllipses style={{ width: 18, height: 18, color: 'var(--color-text-muted, #879494)' }} />
          </ContextButton>
        </Popover>
      </Space>
    );
  };

  const manageTitle = (isOrg: boolean) => (
    <>
      {isOrg ? t('users.manage-orgs') : t('users.manage-users')}{' '}
      <Tooltip
        title={
          <>
            {t('users.manage-tooltip-intro')}
            <ul>
              <li>{t(isOrg ? 'users.manage-orgs-item-1' : 'users.manage-users-item-1')}</li>
              <li>{t(isOrg ? 'users.manage-orgs-item-2' : 'users.manage-users-item-2')}</li>
              <li>{t(isOrg ? 'users.manage-orgs-item-3' : 'users.manage-users-item-3')}</li>
              <li>{t(isOrg ? 'users.manage-orgs-item-4' : 'users.manage-users-item-4')}</li>
            </ul>
          </>
        }
      >
        <ExclamationCircleOutlined />
      </Tooltip>
    </>
  );

  const columns: ColumnsType<SharedUser> = [
    {
      title: t('users.user'),
      dataIndex: 'firstName',
      key: 'fullName',
      render: (_, record) => renderUserIdentity(record),
      sorter: () => 0,
    },
    {
      title: t('users.accountType'),
      dataIndex: 'accountType',
      key: 'accountType',
      render: (_, record) => <AccountTypeTag accountTypeKey={record.accountType} t={t} />,
      sorter: () => 0,
    },
    {
      title: t('users.last-login'),
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      render: (_, record) => lastLoginText(record),
      sorter: () => 0,
    },
    {
      title: t('organisation.created-by'),
      dataIndex: 'accountCreatorName',
      key: 'accountCreatorName',
      render: (_, record) => {
        if (isOwnerAccount(record) || !record.accountCreatorName?.trim()) {
          return null;
        }
        const names = record.accountCreatorName.trim().split(' ');
        return (
          <Space>
            {renderAvatar(
              names[0],
              names[1] || '',
              AvatarSizes.DEFAULT,
            )}
            <BoldNameSpan fontWeight={400}>{record.accountCreatorName}</BoldNameSpan>
          </Space>
        );
      },
    },
    {
      title: t('main.status'),
      dataIndex: 'accountActivated',
      key: 'accountActivated',
      render: (_, record) => renderStatus(record),
    },
    {
      title: (
        <Space>
          {hasManagerViewAccess ? (
            t('other.manager')
          ) : (
            <Space style={{ cursor: 'pointer' }} onClick={onUpgradeClick}>
              {t('other.manager')}
              <LockFilled style={{ fontSize: 12 }} />
            </Space>
          )}
          <Tooltip title={t('other.manager-view-description')}>
            <ExclamationCircleOutlined />
          </Tooltip>
        </Space>
      ),
      key: 'managerViewEnabled',
      onCell: () => ({
        onClick: (event: MouseEvent) => event.stopPropagation(),
      }),
      render: (_, r) =>
        currentUser?.userUuid !== r.userUuid &&
        staffSwitch(
          r,
          'manager',
          PermissionType.MANAGER,
          t('other.manager'),
          !hasManagerViewAccess,
        ),
    },
    {
      title: manageTitle(true),
      key: 'canManageOrgs',
      onCell: () => ({
        onClick: (event: MouseEvent) => event.stopPropagation(),
      }),
      render: (_, r) =>
        currentUser?.userUuid !== r.userUuid &&
        staffSwitch(r, 'orgs', PermissionType.ORGANISATIONS, t('users.manage-orgs')),
    },
    {
      title: manageTitle(false),
      key: 'canManageUsers',
      onCell: () => ({
        onClick: (event: MouseEvent) => event.stopPropagation(),
      }),
      render: (_, r) =>
        currentUser?.userUuid !== r.userUuid &&
        staffSwitch(r, 'users', PermissionType.USERS, t('users.manage-users')),
    },
    {
      key: 'actions',
      render: (_, record) => renderButtons(record),
    },
  ];

  const tableColumns = columns.map((column) => {
    if (!column.sorter) return column;
    const isSorted = column.key === sortState.columnKey;
    return {
      ...column,
      sortDirections: ['ascend', 'descend', 'ascend'] as ('ascend' | 'descend')[],
      sortOrder: isSorted ? sortState.order : null,
      sortIcon: renderSortIcon,
    };
  });

  const onTableChange: TableProps<SharedUser>['onChange'] = (
    _pagination,
    _filters,
    sorter,
  ) => {
    const active = Array.isArray(sorter) ? sorter[0] : sorter;
    const rawKey = active?.columnKey ?? active?.column?.key ?? active?.field;
    const columnKeyAliases: Record<string, string> = {
      firstName: 'fullName',
    };
    const resolvedKey = rawKey != null ? String(rawKey) : sortState.columnKey;
    const columnKey = columnKeyAliases[resolvedKey] ?? resolvedKey;
    let nextOrder: 'ascend' | 'descend' | undefined =
      active?.order === 'ascend' || active?.order === 'descend'
        ? active.order
        : undefined;

    if (!nextOrder && columnKey === sortState.columnKey) {
      nextOrder = sortState.order === 'ascend' ? 'descend' : 'ascend';
    }
    if (!nextOrder) nextOrder = 'ascend';
    if (
      !columnKey ||
      (columnKey === sortState.columnKey && nextOrder === sortState.order)
    ) {
      return;
    }
    setSortState({ columnKey, order: nextOrder });
  };

  return (
    <div
      className="users-management-root"
      style={{
        padding: '10px 40px 40px',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        margin: 0,
        boxSizing: 'border-box',
      }}
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : (
        <Row justify="center" gutter={[0, 16]}>
          <Col span={24}>
            <div
              className="users-list-toolbar"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8,
                marginBottom: 10,
              }}
            >
              <UsersTableFilters
                members={members ?? []}
                value={filtersState}
                onChange={setFiltersState}
              />
              <div
                className="users-list-toolbar-actions"
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Space wrap size={10} align="center">
                  <Segmented
                    className="users-view-segmented"
                    value={viewMode}
                    onChange={(v) => onViewModeChange(v as UsersViewMode)}
                    options={[
                      { label: t('dashboard.view-list'), value: 'list' },
                      { label: t('users.view-card'), value: 'card' },
                    ]}
                  />
                  {currentUser?.canManageUsers && (
                    <ScopeButton
                      className="users-add-user"
                      width="auto"
                      height="32px"
                      fontSize="12px"
                      lineheight="12px"
                      onClick={openMemberWindow}
                      type="primary"
                    >
                      {t('users.add-user')}
                    </ScopeButton>
                  )}
                </Space>
              </div>
            </div>
            {viewMode === 'list' && (
              <Table
                className="users-scope-table"
                rowKey="accountUuid"
                columns={tableColumns}
                dataSource={displayMembers}
                sortDirections={['ascend', 'descend', 'ascend']}
                onChange={onTableChange}
                scroll={{ x: 'max-content' }}
                pagination={{
                  pageSize,
                  showSizeChanger: {
                    className: 'scope-table-page-size-select',
                    classNames: {
                      popup: { root: 'scope-table-page-size-dropdown' },
                    },
                  },
                  pageSizeOptions: ['10', '20', '50', '100'],
                  onShowSizeChange: (_, size) => onPageSizeChange(size),
                }}
              />
            )}
            {viewMode === 'card' && (
              <>
                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                  {paginatedCardMembers.map((record) => (
                    <Col xs={24} sm={12} lg={8} xl={6} key={record.accountUuid}>
                      <Card
                        className="users-card"
                        style={{
                          borderRadius: 10,
                          border: '1px solid var(--color-border, #dbeae3)',
                          height: '100%',
                          background: 'var(--color-surface-raised, #ffffff)',
                        }}
                        styles={{ body: { padding: 24 } }}
                      >
                        <div className="users-card-header" style={{ display: 'flex', gap: 12 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {renderUserIdentity(record, true)}
                            <Space wrap style={{ marginTop: 8 }}>
                              <AccountTypeTag accountTypeKey={record.accountType} t={t} />
                              {renderStatus(record)}
                            </Space>
                          </div>
                          <div>{renderButtons(record)}</div>
                        </div>
                        <Divider style={{ margin: '20px 0 14px' }} />
                        <div>
                          {t('users.last-login')}: {lastLoginText(record)}
                        </div>
                        {!isOwnerAccount(record) && record.accountCreatorName?.trim() ? (
                          <div>
                            {t('organisation.created-by')}: {record.accountCreatorName}
                          </div>
                        ) : null}
                      </Card>
                    </Col>
                  ))}
                </Row>
                {displayMembers.length > 0 && (
                  <Pagination
                    className="users-card-pagination"
                    style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}
                    current={cardPage}
                    pageSize={pageSize}
                    total={displayMembers.length}
                    onChange={setCardPage}
                    showSizeChanger={{
                      className: 'scope-table-page-size-select',
                      classNames: {
                        popup: { root: 'scope-table-page-size-dropdown' },
                      },
                    }}
                    pageSizeOptions={['10', '20', '50', '100']}
                    onShowSizeChange={(_, size) => onPageSizeChange(size)}
                  />
                )}
              </>
            )}
          </Col>
        </Row>
      )}
    </div>
  );
};
